import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import axiosInstance from "../AxiosInstance";
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

const CustomerDropdown = ({ onCustomerSelected, channelSelectable = false }) => {
    const [customerGroups, setCustomerGroups] = useState([]);

    useEffect(() => {
        fetchGroupedCustomers();
    }, []);

    const fetchGroupedCustomers = async () => {
        try {
            const [channelsResponse, customersResponse] = await Promise.all([
                axiosInstance.get('/channels'),
                axiosInstance.get('/customers')
            ]);
            const channels = channelsResponse.data;
            const customers = customersResponse.data;
            const optionsWithGroup = channels.flatMap(channel => {
                const path = channel.path || channel.name;
                const depth = path.split('/').length;
                return [
                    {
                        channelName: path,
                        name: path,
                        isChannel: true,
                        channelId: channel.channelId,
                        depth
                    },
                    ...customers
                        .filter(customer => customer.channelId === channel.channelId)
                        .map(customer => ({
                            ...customer,
                            channelName: path,
                            isChannel: false,
                            depth: depth + 1
                        }))
                ];
            });
            const uncategorisedCustomers = customers
                .filter(customer => customer.channelId == null)
                .map(customer => ({
                    ...customer,
                    channelName: 'Uncategorised',
                    isChannel: false,
                    depth: 2
                }));
            if (uncategorisedCustomers.length > 0) {
                optionsWithGroup.push(
                    {
                        channelName: 'Uncategorised',
                        name: 'Uncategorised',
                        isChannel: true,
                        channelId: null,
                        depth: 1
                    },
                    ...uncategorisedCustomers
                );
            }
            setCustomerGroups(optionsWithGroup);
        } catch (error) {
            console.error('Failed to fetch customers:', error);
        }
    };

    // Custom styles
    const ChannelItem = styled(Typography, {
        shouldForwardProp: prop => prop !== 'depth'
    })(({ theme, depth }) => ({
        color: theme.palette.text.secondary,
        fontWeight: 'bold',
        paddingLeft: theme.spacing(Math.max(1, depth))
    }));

    const CustomerItem = styled(Typography, {
        shouldForwardProp: prop => prop !== 'depth'
    })(({ theme, depth }) => ({
        paddingLeft: theme.spacing(Math.max(2, depth + 1))
    }));

    return (
        <Autocomplete
            id="grouped-customers-dropdown"
            options={customerGroups}
            getOptionLabel={(option) => option.name}
            getOptionDisabled={(option) => option.isChannel && (!channelSelectable || option.channelId == null)}
            renderOption={(props, option) => {
                const key = option.isChannel
                    ? `channel-${option.channelId}`
                    : `customer-${option.customerId}`;

                return (
                    <li {...props} key={key}>
                        {option.isChannel ? (
                            <ChannelItem depth={option.depth}>{option.name}</ChannelItem>
                        ) : (
                            <CustomerItem depth={option.depth}>{option.name}</CustomerItem>
                        )}
                    </li>
                );
            }}
            renderInput={(params) => <TextField {...params} label="Customer" />}
            onChange={(event, value) => {
                if (value === null) {
                    onCustomerSelected({ type: 'clear', id: null, name: null });
                } else if (value?.isChannel) {
                    onCustomerSelected({ type: 'channel', id: value.channelId, name: value.name });
                } else {
                    onCustomerSelected({ type: 'customer', id: value?.customerId, name: value?.name });
                }
            }}
            sx={{ width: 300 }}
        />
    );
};

export default CustomerDropdown;
